import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faPhone,
  faEnvelope,
  faHashtag,
  faLocationDot,
  faIdCard,
  faBuildingColumns,
  faSpinner,
  faArrowLeft,
  faCamera,
  faFileLines,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Alert,
} from "@themesberg/react-bootstrap";
import { toast } from "react-toastify";

import { getSingleUserById, adminUpdateUser } from "../../../pages/api";
import { Routes } from "../../../routes";
import { MEDIA_URL } from "../../helper";

const STATE_OPTIONS = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const CITY_OPTIONS = {
  Maharashtra: ["Mumbai", "Pune", "Nagpur", "Nashik"],
  Karnataka: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi"],
  Gujarat: ["Ahmedabad", "Surat", "Vadodara", "Rajkot"],
  Delhi: ["New Delhi"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Varanasi"],
};

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ALLOWED_PROFILE_PHOTO_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const ALLOWED_ID_PROOF_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

const initialFormData = {
  name: "",
  email: "",
  mobile_number: "",
  // aadhaar_number: "",
  address_line_1: "",
  address_line_2: "",
  state: "",
  city: "",
  pincode: "",
  // account_holder_name: "",
  // bank_name: "",
  // account_number: "",
  // ifsc_code: "",
  profile_photo: "",
  // id_proof: "",
};

const mapApiUserToState = (data = {}) => {
  const bankDetails = data?.bank_details || {};

  return {
    name: data?.name || "",
    email: data?.email || "",
    mobile_number: String(data?.mobile_number || "").replace(/\D/g, "").slice(0, 10),
    // aadhaar_number: String(data?.aadhar_number || data?.aadhaar_number || "")
    //   .replace(/\D/g, "")
    //   .slice(0, 12),
    address_line_1: data?.address_line_1 || "",
    address_line_2: data?.address_line_2 || "",
    state: data?.state || "",
    city: data?.city || "",
    pincode: String(data?.pincode || "").replace(/\D/g, "").slice(0, 6),
    // account_holder_name: bankDetails?.account_holder_name || "",
    // bank_name: bankDetails?.bank_name || "",
    // account_number: String(bankDetails?.account_number || "").replace(/\D/g, "").slice(0, 18),
    // ifsc_code: String(bankDetails?.ifsc_code || "").toUpperCase(),
    profile_photo: data?.profile_photo || "",
    // id_proof: data?.id_proof || "",
  };
};

const getCitiesForState = (state) => CITY_OPTIONS[state] || [];

const isFileObject = (value) => value instanceof File;

const getFileName = (value) => {
  if (!value) return "";
  if (isFileObject(value)) return value.name;
  if (typeof value === "string") return String(value).split("/").pop();
  return "";
};

const getFileUrl = (value) => {
  if (!value) return "";
  if (isFileObject(value)) return URL.createObjectURL(value);
  if (typeof value === "string") {
    const clean = value.startsWith("/") ? value : `/${value}`;
    return `${MEDIA_URL}${clean}`;
  }
  return "";
};

const validateProfilePhotoFile = (file) => {
  if (!file) return "";
  if (!ALLOWED_PROFILE_PHOTO_MIME_TYPES.includes(file.type)) {
    return "Only JPG, PNG, and WebP images are allowed.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size must be 5 MB or less.";
  }
  return "";
};

const validateFile = (file) => {
  if (!file) return "";
  if (!ALLOWED_ID_PROOF_MIME_TYPES.includes(file.type)) {
    return "Only PDF, JPG, and PNG files are allowed.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size must be 5 MB or less.";
  }
  return "";
};

export default function AdminEditUser() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const selectedCities = useMemo(() => getCitiesForState(formData.state), [formData.state]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const response = await getSingleUserById(id);
        const apiUser = response?.data?.data || {};
        setFormData(mapApiUserToState(apiUser));
      } catch (error) {
        console.error("Failed to load user:", error);
        toast.error(error?.response?.data?.message || "Unable to fetch user details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) loadUser();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "ifsc_code" ? value.toUpperCase() : value,
      ...(name === "state" ? { city: "" } : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleMobileChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, mobile_number: nextValue }));
    setErrors((prev) => ({ ...prev, mobile_number: "" }));
  };

  // const handleAadhaarChange = (e) => {
  //   const nextValue = e.target.value.replace(/\D/g, "").slice(0, 12);
  //   setFormData((prev) => ({ ...prev, aadhaar_number: nextValue }));
  //   setErrors((prev) => ({ ...prev, aadhaar_number: "" }));
  // };

  const handlePincodeChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData((prev) => ({ ...prev, pincode: nextValue }));
    setErrors((prev) => ({ ...prev, pincode: "" }));
  };

  const handleAccountNumberChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 18);
    setFormData((prev) => ({ ...prev, account_number: nextValue }));
    setErrors((prev) => ({ ...prev, account_number: "" }));
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateProfilePhotoFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, profile_photo: validationError }));
      toast.error(validationError);
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      profile_photo: file,
    }));
    setErrors((prev) => ({ ...prev, profile_photo: "" }));
  };

  const handleIdProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setErrors((prev) => ({ ...prev, id_proof: validationError }));
      toast.error(validationError);
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      id_proof: file,
    }));
    setErrors((prev) => ({ ...prev, id_proof: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.name.trim()) nextErrors.name = "Name is required.";

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.mobile_number.trim()) {
      nextErrors.mobile_number = "Mobile number is required.";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile_number.trim())) {
      nextErrors.mobile_number = "Enter a valid 10-digit mobile number.";
    }

    // if (!formData.aadhaar_number.trim()) {
    //   nextErrors.aadhaar_number = "Aadhaar number is required.";
    // } else if (!/^\d{12}$/.test(formData.aadhaar_number.trim())) {
    //   nextErrors.aadhaar_number = "Enter a valid 12-digit Aadhaar number.";
    // }

    if (!formData.address_line_1.trim()) nextErrors.address_line_1 = "Address line 1 is required.";
    if (!formData.state.trim()) nextErrors.state = "State is required.";
    if (!formData.city.trim()) nextErrors.city = "City is required.";

    if (!formData.pincode.trim()) {
      nextErrors.pincode = "Pincode is required.";
    } else if (!/^\d{6}$/.test(formData.pincode.trim())) {
      nextErrors.pincode = "Enter a valid 6-digit pincode.";
    }

    // if (!formData.account_holder_name.trim()) {
    //   nextErrors.account_holder_name = "Account holder name is required.";
    // }

    // if (!formData.bank_name.trim()) {
    //   nextErrors.bank_name = "Bank name is required.";
    // }

    // if (!formData.account_number.trim()) {
    //   nextErrors.account_number = "Account number is required.";
    // }

    // if (!formData.ifsc_code.trim()) {
    //   nextErrors.ifsc_code = "IFSC code is required.";
    // }

    if (isFileObject(formData.profile_photo)) {
      const profilePhotoError = validateProfilePhotoFile(formData.profile_photo);
      if (profilePhotoError) nextErrors.profile_photo = profilePhotoError;
    }

    // if (isFileObject(formData.id_proof)) {
    //   const idProofError = validateFile(formData.id_proof);
    //   if (idProofError) nextErrors.id_proof = idProofError;
    // }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = new FormData();
      payload.append("name", formData.name.trim());
      payload.append("email", formData.email.trim());
      payload.append("mobile_number", formData.mobile_number.trim());
      // payload.append("aadhaar_number", formData.aadhaar_number.trim());
      payload.append("address_line_1", formData.address_line_1.trim());
      payload.append("address_line_2", formData.address_line_2.trim());
      payload.append("state", formData.state.trim());
      payload.append("city", formData.city.trim());
      payload.append("pincode", formData.pincode.trim());
      // payload.append("account_number", formData.account_number.trim());
      // payload.append("ifsc_code", formData.ifsc_code.trim());
      // payload.append("bank_name", formData.bank_name.trim());
      // payload.append("account_holder_name", formData.account_holder_name.trim());

      if (isFileObject(formData.profile_photo)) {
        payload.append("profile_photo", formData.profile_photo);
      }

      // if (isFileObject(formData.id_proof)) {
      //   payload.append("id_proof", formData.id_proof);
      // }

      await adminUpdateUser(id, payload);

      toast.success("User updated successfully.");
      setAlertMessage("User details updated successfully.");
      navigate(Routes?.AdminUserManagement?.path || "/admin-user-management");
    } catch (error) {
      console.error("Edit user failed:", error);
      toast.error(error?.response?.data?.message || "Unable to update user.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ overflowX: "hidden", maxWidth: "100%", minWidth: 0 }}>
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          {/* <h2 className="h4 mb-1">Edit User</h2> */}
          <h2 className="h4 mb-1">Edit User</h2>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
            Loading user details...
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div style={{ overflowX: "hidden", maxWidth: "100%", minWidth: 0 }}>
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <div>
            <h2 className="h4 mb-1">Edit User</h2>
          </div>

          <div className="admin-user-header-actions">
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => navigate(-1)}
              className="admin-back-btn"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
          </div>
        </div>

        {/* {alertMessage ? (
          <Alert variant="info" className="border-0 shadow-sm rounded-4">
            {alertMessage}
          </Alert>
        ) : null} */}

        <form onSubmit={handleSubmit}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body className="p-4">
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Name <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.name ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faUser} />
                      </InputGroup.Text>
                      <Form.Control
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        className="border-start-0"
                        placeholder="Enter name"
                      />
                    </InputGroup>
                    {errors.name ? <div className="text-danger small mt-1">{errors.name}</div> : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.email ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faEnvelope} />
                      </InputGroup.Text>
                      <Form.Control
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        className="border-start-0"
                        placeholder="Enter email"
                      />
                    </InputGroup>
                    {errors.email ? <div className="text-danger small mt-1">{errors.email}</div> : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Mobile Number <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.mobile_number ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faPhone} />
                      </InputGroup.Text>
                      <Form.Control
                        name="mobile_number"
                        value={formData.mobile_number}
                        onChange={handleMobileChange}
                        isInvalid={!!errors.mobile_number}
                        className="border-start-0"
                        placeholder="10-digit mobile number"
                        maxLength={10}
                      />
                    </InputGroup>
                    {errors.mobile_number ? (
                      <div className="text-danger small mt-1">{errors.mobile_number}</div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Aadhaar Number <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.aadhaar_number ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faIdCard} />
                      </InputGroup.Text>
                      <Form.Control
                        name="aadhaar_number"
                        value={formData.aadhaar_number}
                        onChange={handleAadhaarChange}
                        isInvalid={!!errors.aadhaar_number}
                        className="border-start-0"
                        placeholder="12-digit Aadhaar number"
                        maxLength={12}
                      />
                    </InputGroup>
                    {errors.aadhaar_number ? (
                      <div className="text-danger small mt-1">{errors.aadhaar_number}</div>
                    ) : null}
                  </Form.Group>
                </Col> */}

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Address Line 1 <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.address_line_1 ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faLocationDot} />
                      </InputGroup.Text>
                      <Form.Control
                        name="address_line_1"
                        value={formData.address_line_1}
                        onChange={handleChange}
                        isInvalid={!!errors.address_line_1}
                        className="border-start-0"
                        placeholder="Enter address line 1"
                      />
                    </InputGroup>
                    {errors.address_line_1 ? (
                      <div className="text-danger small mt-1">{errors.address_line_1}</div>
                    ) : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Address Line 2</Form.Label>
                    <InputGroup className="flex-nowrap">
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faLocationDot} />
                      </InputGroup.Text>
                      <Form.Control
                        name="address_line_2"
                        value={formData.address_line_2}
                        onChange={handleChange}
                        className="border-start-0"
                        placeholder="Enter address line 2"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      State <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      isInvalid={!!errors.state}
                    >
                      <option value="">Select state</option>
                      {STATE_OPTIONS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.state ? <div className="text-danger small mt-1">{errors.state}</div> : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="city"
                      list="city-options"
                      value={formData.city}
                      onChange={handleChange}
                      isInvalid={!!errors.city}
                      placeholder="Enter city"
                    />
                    <datalist id="city-options">
                      {selectedCities.map((item) => (
                        <option key={item} value={item} />
                      ))}
                    </datalist>
                    {errors.city ? <div className="text-danger small mt-1">{errors.city}</div> : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Pincode <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.pincode ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faHashtag} />
                      </InputGroup.Text>
                      <Form.Control
                        name="pincode"
                        value={formData.pincode}
                        onChange={handlePincodeChange}
                        isInvalid={!!errors.pincode}
                        className="border-start-0"
                        placeholder="6-digit pincode"
                        maxLength={6}
                      />
                    </InputGroup>
                    {errors.pincode ? <div className="text-danger small mt-1">{errors.pincode}</div> : null}
                  </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Profile Photo Upload</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={handleProfilePhotoChange}
                      isInvalid={!!errors.profile_photo}
                    />
                    <div className="text-muted small mt-1">
                      Allowed: JPG, PNG, WEBP. Max size: 5 MB.
                    </div>
                    {errors.profile_photo ? (
                      <div className="text-danger small mt-1">{errors.profile_photo}</div>
                    ) : null}

                    {formData.profile_photo ? (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                          <div className="fw-semibold">View Profile Photo</div>
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              window.open(
                                getFileUrl(formData.profile_photo),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            View
                          </Button>
                        </div>

                        {/* <div className="d-flex align-items-center gap-3 p-3 rounded-4 border bg-light">
                          <div
                            className="rounded-circle overflow-hidden border bg-white d-flex align-items-center justify-content-center"
                            style={{ width: 64, height: 64, minWidth: 64 }}
                          >
                            {String(formData.profile_photo || "").match(/\.(png|jpe?g|webp)$/i) ? (
                              <img
                                src={getFileUrl(formData.profile_photo)}
                                alt="Profile"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <FontAwesomeIcon icon={faCamera} className="text-primary" />
                            )}
                          </div>

                          <div className="text-break">
                            <div className="fw-semibold">
                              {getFileName(formData.profile_photo) || "Current profile photo"}
                            </div>
                            <div className="text-muted small">Preview available</div>
                          </div>
                        </div> */}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                {/* <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">ID Proof Upload</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleIdProofChange}
                      isInvalid={!!errors.id_proof}
                    />
                    <div className="text-muted small mt-1">
                      Allowed: PDF, JPG, PNG. Max size: 5 MB.
                    </div>
                    {errors.id_proof ? (
                      <div className="text-danger small mt-1">{errors.id_proof}</div>
                    ) : null}

                    {formData.id_proof ? (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                          <div className="fw-semibold">View Document</div>
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              window.open(
                                getFileUrl(formData.id_proof),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            View
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </Form.Group>
                </Col> */}

                {/* <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Account Holder Name <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.account_holder_name ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faBuildingColumns} />
                      </InputGroup.Text>
                      <Form.Control
                        name="account_holder_name"
                        value={formData.account_holder_name}
                        onChange={handleChange}
                        isInvalid={!!errors.account_holder_name}
                        className="border-start-0"
                        placeholder="Enter account holder name"
                      />
                    </InputGroup>
                    {errors.account_holder_name ? (
                      <div className="text-danger small mt-1">{errors.account_holder_name}</div>
                    ) : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Bank Name <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup className={`flex-nowrap ${errors.bank_name ? "invalid-input-group" : ""}`}>
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon={faBuildingColumns} />
                      </InputGroup.Text>
                      <Form.Control
                        name="bank_name"
                        value={formData.bank_name}
                        onChange={handleChange}
                        isInvalid={!!errors.bank_name}
                        className="border-start-0"
                        placeholder="Enter bank name"
                      />
                    </InputGroup>
                    {errors.bank_name ? <div className="text-danger small mt-1">{errors.bank_name}</div> : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Account Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="account_number"
                      value={formData.account_number}
                      onChange={handleAccountNumberChange}
                      isInvalid={!!errors.account_number}
                      placeholder="Enter account number"
                    />
                    {errors.account_number ? (
                      <div className="text-danger small mt-1">{errors.account_number}</div>
                    ) : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      IFSC Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={handleChange}
                      isInvalid={!!errors.ifsc_code}
                      placeholder="Enter IFSC code"
                      style={{ textTransform: "uppercase" }}
                    />
                    {errors.ifsc_code ? <div className="text-danger small mt-1">{errors.ifsc_code}</div> : null}
                  </Form.Group>
                </Col> */}
              </Row>

              <div className="d-flex justify-content-end mt-4">
                <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Updating...
                    </>
                  ) : (
                    "Update User"
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </form>
      </div>

      <style>{`
        .admin-back-btn {
          width: 44px;
          height: 44px;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .invalid-input-group .form-control,
        .invalid-input-group .input-group-text,
        .invalid-input-group .form-select {
          border-color: #dc3545 !important;
        }

        .invalid-input-group {
          border-radius: 0.5rem;
        }

        .form-control,
        .form-select,
        .input-group-text {
          border-radius: 0.5rem;
        }

        .form-control,
        .form-select {
          min-height: 46px;
        }

        .input-group-text {
          background: #ffffff;
          color: #9aa7bd;
          min-width: 46px;
          justify-content: center;
        }

        .form-control::placeholder {
          color: #9aa7bd;
        }

        @media (max-width: 767.98px) {
          .admin-back-btn {
            width: 40px;
            height: 40px;
          }

          .form-control,
          .form-select {
            min-height: 44px;
          }
        }
      `}</style>
    </>
  );
}